"use client";

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch } from "../../../../../../app/store";
import { RootState } from "../../../../../../app/rootReducer";
import { fetchCompanyProfileAction } from "../../slices/companyProfileSlice";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ReadOnlyFieldProps {
  label: string;
  value?: string | null;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
  <div>
    <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-1 text-sm text-gray-900 dark:text-white">
      {value && value.trim() !== "" ? value : "—"}
    </p>
  </div>
);

const CompanyProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { companyProfile } = useSelector(
    (state: RootState) => state.companyProfile,
  );

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
  }, [dispatch]);

  const isMailingSame = companyProfile?.isMailingAddressSame ?? true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Profile</CardTitle>
        <CardDescription>
          Your company details are synced from Salesforce and managed there. To
          update this information, contact your CertainPath representative.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <ReadOnlyField
            label="Company Name"
            value={companyProfile?.companyName}
          />
          <ReadOnlyField
            label="Company Email"
            value={companyProfile?.companyEmail}
          />
          <ReadOnlyField
            label="Website URL"
            value={companyProfile?.websiteUrl}
          />
          <ReadOnlyField
            label="Address Line 1"
            value={companyProfile?.addressLine1}
          />
          <ReadOnlyField
            label="Address Line 2"
            value={companyProfile?.addressLine2}
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <ReadOnlyField label="City" value={companyProfile?.city} />
            <ReadOnlyField label="Country" value={companyProfile?.country} />
            <ReadOnlyField
              label="State / Province"
              value={companyProfile?.state}
            />
          </div>
          <ReadOnlyField
            label="ZIP / Postal Code"
            value={companyProfile?.zipCode}
          />

          {!isMailingSame && (
            <div className="space-y-6 pt-4 border-t dark:border-slate-700">
              <ReadOnlyField
                label="Mailing Address Line 1"
                value={companyProfile?.mailingAddressLine1}
              />
              <ReadOnlyField
                label="Mailing Address Line 2"
                value={companyProfile?.mailingAddressLine2}
              />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <ReadOnlyField
                  label="Mailing Country"
                  value={companyProfile?.mailingCountry}
                />
                <ReadOnlyField
                  label="Mailing State / Province"
                  value={companyProfile?.mailingState}
                />
                <ReadOnlyField
                  label="Mailing ZIP / Postal Code"
                  value={companyProfile?.mailingZipCode}
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyProfile;
